import { Badge } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { BodyText } from "@/components/ui/Text";
import { useState } from "react";
import type { AVCallSuggestedSnomedCode } from "../../network";

interface AVCallSuggestedConditionPopoverProps {
  snomedCodes: AVCallSuggestedSnomedCode[];
  onAdd: (code: AVCallSuggestedSnomedCode) => void;
  isPending: boolean;
  addedCode?: AVCallSuggestedSnomedCode;
  pendingCodeDisplay?: string;
}

/**
 * Multi-SNOMED-code picker used when an extracted condition resolved to two
 * or more candidates. Provider picks one from a dropdown to add. Same Add /
 * Pending / Added visual transitions as the single-code row.
 */
export const AVCallSuggestedConditionPopover = ({
  snomedCodes,
  onAdd,
  isPending,
  addedCode,
  pendingCodeDisplay,
}: AVCallSuggestedConditionPopoverProps) => {
  const [value, setValue] = useState("");

  if (isPending) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 h-8 border border-transparent rounded-lg">
        <BodyText size="sm">{pendingCodeDisplay}</BodyText>
        <Button
          size="xs"
          type="button"
          isLoading={true}
          className="shrink-0 transition-none bg-button-superclinical text-button-primary border border-ai"
        >
          Add
        </Button>
      </div>
    );
  }

  if (addedCode) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 h-8 border border-transparent rounded-lg">
        <BodyText size="sm">{addedCode.display ?? "—"}</BodyText>
        <div className="flex items-center shrink-0" style={{ height: 25 }}>
          <Badge variant="ai">
            <BodyText size="xs">Added</BodyText>
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={(code) => {
        setValue("");
        const snomedCode = snomedCodes.find((c) => c.code === code);
        if (snomedCode) onAdd(snomedCode);
      }}
    >
      <SelectTrigger className="mt-1">
        <SelectValue placeholder="Select to add..." />
      </SelectTrigger>
      <SelectContent scrollMode="scrollbar">
        {snomedCodes.map((c) =>
          c.code ? (
            <SelectItem key={c.code} value={c.code}>
              {c.display ?? c.code}
            </SelectItem>
          ) : null,
        )}
      </SelectContent>
    </Select>
  );
};
